import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useLayoutEffect } from "react";
import { Layout } from "./widgets";
import AddCompany from "./components/addCompany/AddCompany";
import { getCampaigns } from "./redux/createNewCompany/actions";
import { getSegments } from "./redux/segments/actions";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { initializeDomainConfig } from "./utils/domainUtils";

import {
  AICreatives,
  Balance,
  BalanceHistory,
  BankAudits,
  Company,
  ContactDetails,
  Counters,
  Finance,
  Profile,
  Replenishment,
  SignIn,
  SignUp,
  Statistics,
} from "./modules";
import { PasswordRecovery } from "./modules/PasswordRecovery";
import { SignUpConfirm } from "./modules/SignUpConfirm";
import { SignUpEmailChange } from "./modules/SignUpEmailChange";
import { PasswordResetConfirm } from "./modules/PasswordResetConfirm";
import { userSelectors } from "./redux/user/selectors";
import { getUserInfo, getUserRequisites, getAgencyRequisites } from "./redux/user/actions";
import useNotifier from "./hooks/useNotifier";
import Staff from "./modules/AdminPanel/Staff/Staff";
import Payments from "./modules/AdminPanel/Payments/Payments";
import AdminLayout from "./modules/AdminPanel/AdminMenu/AdminLayouts/AdminLayout";
import Clients from "./modules/AdminPanel/Clients/Clients";
import AdminSignIn from "./modules/AdminPanel/Sign-in/adminSignIn";
import AdminFirstSignIn from "./modules/AdminPanel/Sign-in/FirstSignIn";
import AdminPasswordReset from "./modules/AdminPanel/Sign-in/adminPasswordReset";
import Reports from "./modules/AdminPanel/Reports/Reports";
import Campaigns from "./modules/AdminPanel/Campaigns/Campaings";
import Logs from "./modules/AdminPanel/Logs/Logs";
import Moderation from "./modules/AdminPanel/Moderation/moderation";
import Ssp from "./modules/AdminPanel/Ssp/ssp";
import Reach from "./modules/AdminPanel/ReachSettings/reach";
import Dmp from "./modules/AdminPanel/Dmp/dmp";
import CampaignsReports from "./modules/AdminPanel/ReportsCampaigns/reports";
import ProductAnalytics from "./modules/AdminPanel/ProductAnalytics/ProductAnalytics";
import useAdminUser from "./data/useAdminUser";
import useAgencyUser from "./data/useAgencyUser";
import useUser from "./data/useUser";
import { ModerationNotificationsProvider } from "./hooks/useModerationNotifications";
import Dsp from "./modules/AdminPanel/Dsp/dsp";
import Agencies from "./modules/AdminPanel/Agencies/Agencies";
import AgencyClients from "./modules/AdminPanel/Agencies/AgencyClients";
import AgencyCampaigns from "./modules/AdminPanel/Agencies/AgencyCampaigns";
import AgencyFinance from "./modules/AdminPanel/Agencies/AgencyFinance";
import AgencyRequisites from "./modules/AdminPanel/Agencies/AgencyRequisites";
import { setCurrentPage } from "./redux/pageContext/actions";


const PrivateAdminRoute = ({ children, allowedRole, allowedRoles }) => {
  const { user: adminUser } = useAdminUser();
  const { user: agencyUser } = useAgencyUser();
  const user = adminUser || agencyUser;
  const role = user?.role;
  const rolesList = Array.isArray(allowedRoles)
    ? allowedRoles
    : allowedRole
      ? [allowedRole]
      : [];

  if (rolesList.length > 0 && !rolesList.includes(role)) {
    return <Navigate to="/admin" />;
  }

  return children;
};

const AdminRedirect = () => {
  const { user: adminUser } = useAdminUser();
  const { user: agencyUser } = useAgencyUser();
  const user = adminUser || agencyUser;
  const role = user?.role;
  const dispatch = useDispatch();
  const { userRequisites } = useSelector(userSelectors.all_object);
  const [isLoadingRequisites, setIsLoadingRequisites] = useState(false);

  useEffect(() => {
    if (role === "AGENCY" && !userRequisites) {
      setIsLoadingRequisites(true);
      dispatch(getAgencyRequisites()).finally(() => {
        setIsLoadingRequisites(false);
      });
    }
  }, [role, userRequisites, dispatch]);

  if (role === "AGENCY") {
    if (isLoadingRequisites) {
      return null;
    }
    if (!userRequisites?.full_name) {
      return <Navigate to="/admin/agency-requisites" />;
    }
    return <Navigate to="/admin/agency-clients" />;
  }

  return <Navigate to="/admin/clients" />;
};


function RouteTracker() {
  const location = useLocation();
  const dispatch = useDispatch();
  useEffect(() => {
    const path = location.pathname;
    const map = (pathname) => {
      if (pathname === "/" || pathname === "") return { id: 1, name: "Главная" };
      if (pathname.startsWith("/statistics")) return { id: 2, name: "Статистика" };
      if (pathname.startsWith("/finance")) return { id: 3, name: "Финансы" };
      if (pathname.startsWith("/counters")) return { id: 4, name: "Счетчики" };
      if (pathname.startsWith("/archive-campaigns")) return { id: 5, name: "Архивные кампании" };
      if (pathname.startsWith("/profile/contact-details")) return { id: 6, name: "Профиль — общая информация" };
      if (pathname.startsWith("/profile/bank-audits")) return { id: 7, name: "Профиль — банковские реквизиты" };
      if (pathname.startsWith("/balance")) return { id: 8, name: "Баланс" };
      if (pathname.startsWith("/sign-up")) return { id: 21, name: "Регистрация" };
      if (pathname.startsWith("/sign-in")) return { id: 22, name: "Войти (логин)" };
      return { id: null, name: "" };
    };
    const page = map(path);
    dispatch(setCurrentPage({ id: page.id, route: path, meta: { name: page.name } }));
  }, [location.pathname]);
  return null;
}

function ModerationNotificationsBridge({ children }) {
  const location = useLocation();
  const { user: adminUser } = useAdminUser();
  const { user: regularUser } = useUser();
  const isAdminArea = location.pathname.startsWith("/admin");
  const email = isAdminArea ? adminUser?.email : regularUser?.email;

  return (
    <ModerationNotificationsProvider email={email}>
      {children}
    </ModerationNotificationsProvider>
  );
}


function App() {
  const [setIsLoggedOut] = useState(false);
  const timerId = useRef(null);

  // Инициализация конфигурации домена
  useEffect(() => {
    initializeDomainConfig();
  }, []);

  useEffect(() => {
    const autoLogout = () => {
      const programmatic_save_password = JSON.parse(localStorage.getItem('programmatic_save_password'));
      // browser tab is inactive and flag 'save password' is NOT set
      if (document.visibilityState === "hidden" && !programmatic_save_password) {
        // set timer to log user out in 30 minutes
        const id = window.setTimeout(() => {
          setIsLoggedOut(true);
          localStorage.removeItem('token');
        }, 30 * 60 * 1000);
        timerId.current = id;
      } else {
        // clear existing timer
        window.clearTimeout(timerId.current);
      }
    };

    document.addEventListener("visibilitychange", autoLogout);

    return () => document.removeEventListener("visibilitychange", autoLogout);
  }, []);

  useNotifier();
  const dispatch = useDispatch();
  const user = useSelector(userSelectors.all_object);

  useLayoutEffect(() => {
    const isAdminArea = window.location.pathname.startsWith("/admin");
    if (isAdminArea) {
      return;
    }
    if (!Object.keys(user).length) {
      dispatch(getUserInfo());
      const agencyToken = localStorage.getItem("agency_token");
      const isAgencyPage = window.location.pathname.startsWith("/admin/agency-");
      if (isAgencyPage && agencyToken) {
        dispatch(getAgencyRequisites());
      } else {
        dispatch(getUserRequisites());
      }
    }
    dispatch(getCampaigns());
    dispatch(getSegments());
  }, [user]);

  return (
    <BrowserRouter>
      <ModerationNotificationsBridge>
      <RouteTracker />
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route path="/" element={<Company />} />

          <Route path="add-company" element={<AddCompany />}></Route>

          <Route path="statistics" element={<Statistics />} />

          <Route path="finance" element={<Finance />}>
            <Route path="replenishment" element={<Replenishment />} />
            <Route path="balance-history" element={<BalanceHistory />} />
            <Route path="closing-documents" element={<div />} />
          </Route>

          <Route path="counters" element={<Counters />} />
          <Route path="archive-campaigns" element={<Company isArchive={true} />} />
          <Route path="ai-creatives" element={<AICreatives />} />

          <Route path="profile" element={<Profile />}>
            <Route path="contact-details" element={<ContactDetails />} />
            <Route path="bank-audits" element={<BankAudits />} />
            {/* <Route path="settings" element={<Settings />} /> */}
          </Route>

          <Route path="balance" element={<Balance />} />
        </Route>

        <Route path="sign-in" element={<SignIn />} />
        <Route path="sign-up/manager" element={<SignUp />} />
        <Route path="sign-up" element={<SignUp />} />
        <Route path="password-recovery" element={<PasswordRecovery />} />
        <Route
          path="password/reset/confirm"
          element={<PasswordResetConfirm />}
        />
        <Route
          path="sign-up/confirmation"
          element={<SignUpConfirm />}
        />
        <Route path="sign-up/email/change" element={<SignUpEmailChange />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route path="/admin" element={<AdminRedirect />} />
          <Route
            path="/admin/clients"
            element={
              <PrivateAdminRoute allowedRoles={["MANAGER", "MANAGER_RETARGEO", "ADMIN", "SUPERUSER"]}>
                <Clients />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/staff"
            element={
              <PrivateAdminRoute allowedRoles={["MANAGER", "ADMIN", "SUPERUSER"]}>
                <Staff />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/payments"
            element={
              <PrivateAdminRoute allowedRoles={["MANAGER", "ADMIN", "SUPERUSER"]}>
                <Payments />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/campaigns"
            element={
              <PrivateAdminRoute allowedRoles={["MANAGER", "ADMIN", "SUPERUSER"]}>
                <Campaigns />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <PrivateAdminRoute allowedRoles={["MANAGER", "ADMIN", "SUPERUSER"]}>
                <Reports />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/logs"
            element={
              <PrivateAdminRoute allowedRoles={["MANAGER", "ADMIN", "SUPERUSER"]}>
                <Logs />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/moderation"
            element={
              <PrivateAdminRoute allowedRoles={["MANAGER", "ADMIN", "SUPERUSER"]}>
                <Moderation />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/ssp"
            element={
              <PrivateAdminRoute allowedRoles={["MANAGER", "ADMIN", "SUPERUSER"]}>
                <Ssp />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/dmp"
            element={
              <PrivateAdminRoute allowedRoles={["MANAGER", "ADMIN", "SUPERUSER"]}>
                <Dmp />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/campaigns-reports"
            element={
              <PrivateAdminRoute allowedRoles={["MANAGER", "ADMIN", "SUPERUSER"]}>
                <CampaignsReports />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/agencies"
            element={
              <PrivateAdminRoute allowedRoles={["ADMIN", "SUPERUSER"]}>
                <Agencies />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/agency-clients"
            element={
              <PrivateAdminRoute allowedRoles={["ADMIN", "SUPERUSER", "AGENCY"]}>
                <AgencyClients />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/agency-campaigns"
            element={
              <PrivateAdminRoute allowedRoles={["ADMIN", "SUPERUSER", "AGENCY"]}>
                <AgencyCampaigns />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/agency-finance"
            element={
              <PrivateAdminRoute allowedRoles={["ADMIN", "SUPERUSER", "AGENCY"]}>
                <AgencyFinance />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/agency-requisites"
            element={
              <PrivateAdminRoute allowedRoles={["ADMIN", "SUPERUSER", "AGENCY"]}>
                <AgencyRequisites />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/reachSettings"
            element={
              <PrivateAdminRoute allowedRole="SUPERUSER">
                <Reach />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/dspSettings"
            element={
              <PrivateAdminRoute allowedRole="SUPERUSER">
                <Dsp />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/closing-documents"
            element={
              <PrivateAdminRoute allowedRoles={["ADMIN", "SUPERUSER"]}>
                <Reports mode="closing-documents" />
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin/product-analytics"
            element={
              <PrivateAdminRoute allowedRole="SUPERUSER">
                <ProductAnalytics />
              </PrivateAdminRoute>
            }
          />
        </Route>
        <Route
          path="/admin/product-analytics-preview"
          element={
            <div style={{ minHeight: "100vh", background: "#f0f1f3" }}>
              <ProductAnalytics />
            </div>
          }
        />
        <Route path="admin/sign-in" element={<AdminSignIn />} />
        <Route path="admin/sign-up/confirmation" element={<AdminFirstSignIn />} />
        <Route path="admin/password-reset" element={<AdminPasswordReset />} />
      </Routes>
      </ModerationNotificationsBridge>
    </BrowserRouter>
  );
}

export default App;
